<script setup lang="ts">
/* Sign in. No sign-up link: accounts are set up by River Atlas, and
   public sign-up is switched off in the Supabase project. */
import { computed, ref } from 'vue'
import Alert from '@/components/ui/Alert.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import { appConfig } from '@/config/app.config'
import { AuthError } from '@/lib/auth/types'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'

useDocumentTitle('Sign in')

const { signIn } = useAuth()

const email = ref('')
const password = ref('')
const error = ref<Error | null>(null)
const submitting = ref(false)

function fieldError(field: string): string | undefined {
  const caught = error.value
  return caught instanceof AuthError && caught.field === field ? caught.message : undefined
}

const generalError = computed(() => {
  const caught = error.value
  if (!caught) return null
  return caught instanceof AuthError && caught.field ? null : caught.message
})

async function handleSubmit() {
  error.value = null
  submitting.value = true

  try {
    // The session watcher in src/router/index.ts does the redirect,
    // honouring `?redirect=` if the guard put one there.
    await signIn({ email: email.value, password: password.value })
  } catch (caught) {
    error.value = caught instanceof Error ? caught : new Error('Something went wrong. Try again.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div>
    <header class="head">
      <h1 class="title">Sign in</h1>
      <p class="subtitle">Welcome back. Manage your infrastructure, fleet and bookings.</p>
    </header>

    <Alert v-if="generalError" tone="danger" class="alert">{{ generalError }}</Alert>

    <form class="form" novalidate @submit.prevent="handleSubmit">
      <TextField
        v-model="email"
        label="Email"
        type="email"
        name="email"
        autocomplete="email"
        placeholder="you@company.eu"
        :error="fieldError('email')"
        required
        autofocus
      />

      <TextField
        v-model="password"
        label="Password"
        type="password"
        name="password"
        autocomplete="current-password"
        placeholder="••••••••"
        :error="fieldError('password')"
        required
      />

      <Button type="submit" full-width :loading="submitting" class="submit">Sign in</Button>
    </form>

    <p class="switch">
      No account yet? Accounts are set up by River Atlas —
      <a :href="`mailto:${appConfig.supportEmail}`">{{ appConfig.supportEmail }}</a>.
    </p>
  </div>
</template>

<style scoped src="./auth.css"></style>
